import React, { useState } from "react";
import { IconButton, useToast } from "components/ui";
import { useTranslation } from "lib/i18n/use-translation";
import { logError } from "utility/Function";
import useMessageFeedback from "apps/user/learner/hooks/use-message-feedback";
import { FeedbackState } from "../types";
import OriFeedbackModal from "./OriFeedbackModal";
import { ReactComponent as ThumbUpDefault } from "Assets/icon/thumb-up-default.svg";
import { ReactComponent as ThumbUpSelected } from "Assets/icon/thumb-up-selected.svg";
import { ReactComponent as ThumbDownDefault } from "Assets/icon/thumb-down-default.svg";
import { ReactComponent as ThumbDownSelected } from "Assets/icon/thumb-down-selected.svg";
import { ReactComponent as FlagDefault } from "Assets/icon/icon-flag-default.svg";
import { ReactComponent as FlagFilled } from "Assets/icon/icon-flag-filled.svg";
import { ReactComponent as CopyIcon } from "Assets/icon/copy.svg";
import styles from "./OriMessageFeedback.module.scss";

interface OriMessageFeedbackProps {
  messageId: string;
  content: string;
  initialFeedback?: FeedbackState | null;
}

const OriMessageFeedback: React.FC<OriMessageFeedbackProps> = ({
  messageId,
  content,
  initialFeedback = null,
}) => {
  const { t } = useTranslation(["ori"]);
  const { openToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { feedback, isSubmitting, submitVote, submitComment, deleteComment } =
    useMessageFeedback({ messageId, initialFeedback });

  const currentVote = feedback?.vote ?? null;
  const hasComment = feedback?.has_text ?? false;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (error) {
      logError("Ori message copy failed", error);
      openToast({
        message: t("ori:chat.toast.genericError"),
        variant: "error",
      });
      return;
    }
    openToast({ message: t("ori:chat.toast.messageCopied") });
  };

  const handleSubmitComment = async (text: string) => {
    await submitComment(text);
    setIsModalOpen(false);
  };

  const handleDeleteComment = async () => {
    await deleteComment();
    setIsModalOpen(false);
  };

  return (
    <>
      <div
        className={styles.row}
        role="toolbar"
        aria-label={t("ori:chat.feedback.a11yToolbar")}
        data-ori-feedback-row
        data-testid="ori-message-feedback"
      >
        <IconButton
          icon={currentVote === "up" ? <ThumbUpSelected /> : <ThumbUpDefault />}
          aria-label={t("ori:chat.reaction.thumbsUp")}
          aria-pressed={currentVote === "up"}
          className={styles.button}
          disabled={isSubmitting}
          onClick={() => submitVote(currentVote === "up" ? null : "up")}
          data-testid="ori-feedback-thumb-up"
        />
        <IconButton
          icon={
            currentVote === "down" ? (
              <ThumbDownSelected />
            ) : (
              <ThumbDownDefault />
            )
          }
          aria-label={t("ori:chat.reaction.thumbsDown")}
          aria-pressed={currentVote === "down"}
          className={styles.button}
          disabled={isSubmitting}
          onClick={() => submitVote(currentVote === "down" ? null : "down")}
          data-testid="ori-feedback-thumb-down"
        />
        <IconButton
          icon={hasComment ? <FlagFilled /> : <FlagDefault />}
          aria-label={
            hasComment
              ? t("ori:chat.reaction.editFeedback")
              : t("ori:chat.reaction.addFeedback")
          }
          className={styles.button}
          disabled={isSubmitting}
          onClick={() => setIsModalOpen(true)}
          data-testid="ori-feedback-comment"
        />
        <IconButton
          icon={<CopyIcon />}
          aria-label={t("ori:chat.reaction.copy")}
          className={styles.button}
          onClick={handleCopy}
          data-testid="ori-feedback-copy"
        />
      </div>
      {isModalOpen && (
        <OriFeedbackModal
          existingText={feedback?.feedback_text ?? ""}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmitComment}
          onDelete={handleDeleteComment}
          onDismiss={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default OriMessageFeedback;
